import { useEffect, useMemo, useState } from 'react'
import { Layout, Segmented, Select } from 'antd'
import { Content, Header } from 'antd/es/layout/layout'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import styles from './Performance.module.scss'
import Loading from '../../../components/Loading/Loading'
import Table, { IColumn, TableRow } from '../../../components/Table'
import {
  getPerformancesAsync,
  getPerformanceTimeseriesAsync,
  ITimeseriesSeries,
} from '../../../redux/admin/performance'
import { useAppDispatch, useAppSelector } from '../../../redux/store'
import { StatusEnum } from '../../../redux/type'
import { notifyError } from '../../../utils/Notification'

type PeriodOption = 'All Time' | 'Last 30 Days'
type ViewMode = 'Table' | 'Graph'
type MetricOption = 'conversion' | 'retention' | 'retarget' | 'retargetConvert' | 'rating'

const periodMap: Record<PeriodOption, 'all' | '30d'> = {
  'All Time': 'all',
  'Last 30 Days': '30d',
}

const metricOptions: Array<{ label: string; value: MetricOption }> = [
  { label: 'Conversion', value: 'conversion' },
  { label: 'Retention', value: 'retention' },
  { label: 'Retarget', value: 'retarget' },
  { label: 'Retarget Convert', value: 'retargetConvert' },
  { label: 'Rating', value: 'rating' },
]

const LINE_COLORS = [
  '#5B8FF9',
  '#5AD8A6',
  '#F6BD16',
  '#E86452',
  '#6DC8EC',
  '#945FB9',
  '#FF9845',
  '#1E9493',
  '#FF99C3',
  '#269A99',
]
const FIRM_AVG_COLOR = '#FFFFFF'

const capitalize = (str: string) => {
  if (!str) return ''
  return str
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

const formatPct = (ratio: number, benchmark: number) => {
  return `${Math.round(ratio * 100)}% | ${Math.round(benchmark * 100)}%`
}

const formatRating = (rating: number, benchmark: number) => {
  return `${rating.toFixed(1)} | ${benchmark.toFixed(1)}`
}

const isPercentageMetric = (metric: MetricOption): boolean => {
  return metric !== 'rating'
}

const formatDateLabel = (dateStr: string): string => {
  const date = new Date(dateStr)
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${months[date.getMonth()]} ${date.getDate()}`
}

const formatTooltipValue = (value: number, metric: MetricOption): string => {
  if (isPercentageMetric(metric)) {
    return `${(value * 100).toFixed(1)}%`
  }
  return value.toFixed(2)
}

const formatYAxisTick = (value: number, metric: MetricOption): string => {
  if (isPercentageMetric(metric)) {
    return `${Math.round(value * 100)}%`
  }
  return value.toFixed(1)
}

const Performance = () => {
  const dispatch = useAppDispatch()
  const [period, setPeriod] = useState<PeriodOption>('All Time')
  const [viewMode, setViewMode] = useState<ViewMode>('Table')
  const [selectedMetric, setSelectedMetric] = useState<MetricOption>('conversion')

  const performances = useAppSelector((state) => state.admin.performance.performances)
  const performancesStatus = useAppSelector((state) => state.admin.performance.performancesStatus)
  const timeseries = useAppSelector((state) => state.admin.performance.timeseries)
  const timeseriesStatus = useAppSelector((state) => state.admin.performance.timeseriesStatus)
  const apiError = useAppSelector((state) => state.admin.performance.error)

  const columns: Array<IColumn> = [
    { capitalize: true, displayName: 'Doctor', key: 'doctor', size: 180 },
    { capitalize: true, displayName: 'Studio', key: 'studio', size: 160 },
    { capitalize: false, displayName: 'Adjustments', key: 'totalAdjustments', align: 'right', size: 100 },
    { capitalize: false, displayName: 'New Patients', key: 'newPatients', align: 'right', size: 100 },
    { capitalize: false, displayName: 'Conversion', key: 'conversion', align: 'right', size: 110 },
    { capitalize: false, displayName: 'Retention', key: 'retention', align: 'right', size: 100 },
    { capitalize: false, displayName: 'Retarget', key: 'retarget', align: 'right', size: 100 },
    { capitalize: false, displayName: 'Retarget Convert', key: 'retargetConvert', align: 'right', size: 120 },
    {
      capitalize: false,
      displayName: 'Rating',
      key: 'rating',
      size: 90,
      header: () => <span style={{ display: 'block', textAlign: 'left' }}>Rating</span>,
      cell: (cellProps: any) => <span style={{ display: 'block', textAlign: 'center' }}>{cellProps.getValue()}</span>,
    },
  ]

  const rows: Array<TableRow> = useMemo(() => {
    return performances.map((p) => ({
      id: p.chiropractorId,
      doctor: capitalize(`${p.firstname} ${p.lastname}`),
      studio: capitalize(p.clinicName || ''),
      totalAdjustments: String(p.totalAdjustments),
      newPatients: String(p.newPatientsSeen),
      conversion: formatPct(p.conversionRatio, p.conversionBenchmark),
      retention: formatPct(p.retentionRatio, p.retentionBenchmark),
      retarget: formatPct(p.retargetRatio, p.retargetBenchmark),
      retargetConvert: formatPct(p.retargetConvertRatio, p.retargetConvertBenchmark),
      rating: formatRating(p.personalRating, p.ratingBenchmark),
    }))
  }, [performances])

  // Build chart data: transform series into recharts-compatible format
  const { chartData, seriesKeys } = useMemo(() => {
    if (!timeseries || !timeseries.series || timeseries.series.length === 0) {
      return { chartData: [], seriesKeys: [] as Array<{ key: string; name: string; isFirmAvg: boolean }> }
    }

    const keys: Array<{ key: string; name: string; isFirmAvg: boolean }> = timeseries.series.map(
      (s: ITimeseriesSeries) => ({
        key: s.id,
        name: s.name,
        isFirmAvg: s.id === 'firm-average',
      }),
    )

    // Collect all unique dates across all series
    const dateSet = new Set<string>()
    timeseries.series.forEach((s: ITimeseriesSeries) => {
      s.dataPoints.forEach((dp) => dateSet.add(dp.date))
    })
    const sortedDates = Array.from(dateSet).sort()

    // Build data array for recharts
    const data = sortedDates.map((date) => {
      const point: Record<string, string | number> = { date }
      timeseries.series.forEach((s: ITimeseriesSeries) => {
        const dp = s.dataPoints.find((d) => d.date === date)
        if (dp) {
          point[s.id] = dp.value
        }
      })
      return point
    })

    return { chartData: data, seriesKeys: keys }
  }, [timeseries])

  // Fetch table data
  useEffect(() => {
    if (viewMode === 'Table') {
      dispatch(getPerformancesAsync({ period: periodMap[period] }))
    }
  }, [period, viewMode])

  // Fetch timeseries data
  useEffect(() => {
    if (viewMode === 'Graph') {
      dispatch(getPerformanceTimeseriesAsync({ metric: selectedMetric, days: 90 }))
    }
  }, [viewMode, selectedMetric])

  useEffect(() => {
    if (performancesStatus === StatusEnum.Failed || timeseriesStatus === StatusEnum.Failed) {
      notifyError('Unable to fetch performance data', apiError)
    }
  }, [performancesStatus, timeseriesStatus])

  const getLineColor = (index: number): string => {
    return LINE_COLORS[index % LINE_COLORS.length]
  }

  const renderCustomTooltip = (props: any) => {
    const { active, payload, label } = props
    if (!active || !payload || !label) return null
    return (
      <div className={styles.chartTooltip}>
        <p className={styles.tooltipDate}>{formatDateLabel(label)}</p>
        {payload.map((entry: any) => (
          <p key={entry.dataKey} className={styles.tooltipItem} style={{ color: entry.color }}>
            {entry.name}: {formatTooltipValue(entry.value, selectedMetric)}
          </p>
        ))}
      </div>
    )
  }

  const isGraphLoading = viewMode === 'Graph' && timeseriesStatus === StatusEnum.Pending
  const isTableLoading = viewMode === 'Table' && performancesStatus === StatusEnum.Pending
  const isLoading = isGraphLoading || isTableLoading

  return (
    <Layout>
      <Header
        style={{
          borderBottom: '1px solid #2D2C3C',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ color: '#fff', fontSize: '16px', fontWeight: 600 }}>Doctor Performance</span>
        <div className={styles.headerControls}>
          {viewMode === 'Graph' && (
            <Select
              className={styles.metricSelect}
              value={selectedMetric}
              onChange={(val) => setSelectedMetric(val as MetricOption)}
              options={metricOptions}
              popupClassName={styles.metricDropdown}
              style={{ width: 180 }}
            />
          )}
          {viewMode === 'Table' && (
            <div className={styles.toggleContainer}>
              <Segmented
                options={['All Time', 'Last 30 Days']}
                value={period}
                onChange={(val) => setPeriod(val as PeriodOption)}
              />
            </div>
          )}
          <Segmented options={['Table', 'Graph']} value={viewMode} onChange={(val) => setViewMode(val as ViewMode)} />
        </div>
      </Header>
      <Content>
        <div style={{ padding: 20, height: '96%' }}>
          {isLoading && <Loading style={{ background: 'none' }} />}

          {!isLoading && viewMode === 'Table' && performancesStatus === StatusEnum.Success && (
            <div className={styles.performanceTable}>
              <Table
                columns={columns}
                rows={rows}
                totalPageCount={rows.length}
                currentPage={1}
                onRowClick={() => {}}
                onPageChange={() => {}}
                noDataFoundContent="No performance data available"
              />
            </div>
          )}

          {!isLoading && viewMode === 'Graph' && timeseriesStatus === StatusEnum.Success && (
            <div className={styles.chartContainer}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2D2C3C" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDateLabel}
                    stroke="#9E9CAF"
                    tick={{ fill: '#9E9CAF', fontSize: 12 }}
                    tickLine={{ stroke: '#9E9CAF' }}
                    axisLine={{ stroke: '#2D2C3C' }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tickFormatter={(val) => formatYAxisTick(val, selectedMetric)}
                    stroke="#9E9CAF"
                    tick={{ fill: '#9E9CAF', fontSize: 12 }}
                    tickLine={{ stroke: '#9E9CAF' }}
                    axisLine={{ stroke: '#2D2C3C' }}
                  />
                  <Tooltip content={renderCustomTooltip} />
                  <Legend wrapperStyle={{ color: '#9E9CAF', paddingTop: 10 }} iconType="line" />
                  {seriesKeys.map((series, index) =>
                    series.isFirmAvg ? (
                      <Line
                        key={series.key}
                        type="monotone"
                        dataKey={series.key}
                        name="Firm Average"
                        stroke={FIRM_AVG_COLOR}
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    ) : (
                      <Line
                        key={series.key}
                        type="monotone"
                        dataKey={series.key}
                        name={series.name}
                        stroke={getLineColor(index)}
                        strokeWidth={1.5}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    ),
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {!isLoading && viewMode === 'Graph' && timeseriesStatus === StatusEnum.Success && chartData.length === 0 && (
            <div className={styles.noData}>No timeseries data available</div>
          )}
        </div>
      </Content>
    </Layout>
  )
}

export default Performance
